import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Gift, Users, Share2, Trophy, Check, Sparkles, ExternalLink } from "lucide-react";
import { userApi } from "@/lib/api";
import { fmtMoney, fmtDate, timeAgo } from "@/lib/format";
import { PageHeader, CopyButton, EmptyState } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Referrals() {
  const { data, isLoading } = useQuery({
    queryKey: ["referrals-me"],
    queryFn: () => userApi.get("/referrals/me").then((r) => r.data),
    refetchInterval: 30000,
  });

  const code = data?.code || "\u2014";
  const link = useMemo(() => {
    if (!data?.code) return "";
    return `${window.location.origin}/register?ref=${data.code}`;
  }, [data?.code]);

  const nativeShare = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join RupeeTether",
          text: `Use my code ${code} to sign up for RupeeTether \u2014 we both earn when you make your first deposit.`,
          url: link,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Invite link copied");
      } catch {
        toast.error("Could not share. Copy the link manually.");
      }
    }
  };

  const cfg = data?.config || { first_deposit_reward: 100, lifetime_pct: 5 };
  const earningsINR = data?.earnings?.INR || 0;
  const earningsUSDT = data?.earnings?.USDT || 0;

  return (
    <div data-testid="referrals-page">
      <PageHeader
        title="Refer & earn"
        subtitle="Share your code with friends. Earn when they make their first deposit — and keep earning on every deposit they make, forever."
      />

      <div className="grid gap-5 lg:grid-cols-12">
        {/* ---- Referral code hero ---- */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-7"
        >
          <Card className="relative overflow-hidden border-amber-200 bg-gradient-to-br from-amber-50 via-white to-teal-50/40">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-14 -top-14 h-48 w-48 rounded-full"
              style={{ background: "radial-gradient(circle, hsl(44 86% 60% / 0.3) 0%, transparent 65%)" }}
            />
            <CardContent className="relative p-6 sm:p-8">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/60 bg-amber-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-800">
                <Sparkles className="h-3 w-3" /> Your invite
              </span>
              <h2 className="mt-4 text-2xl font-semibold sm:text-3xl">
                Earn <span className="text-amber-700">{fmtMoney(cfg.first_deposit_reward, "INR")}</span>
                {" "}+ <span className="text-amber-700">{cfg.lifetime_pct}%</span> on every friend
              </h2>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                We credit <span className="font-semibold text-foreground">{fmtMoney(cfg.first_deposit_reward, "INR")}</span>{" "}
                when a friend makes their first approved deposit, and{" "}
                <span className="font-semibold text-foreground">{cfg.lifetime_pct}%</span> of every deposit they make after that
                — for life.
              </p>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="flex-1 rounded-xl border bg-white p-4">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Your referral code</p>
                  <div className="mt-1 flex items-center gap-3">
                    {isLoading ? (
                      <Skeleton className="h-9 w-32" />
                    ) : (
                      <p className="num text-3xl font-bold tracking-wider text-primary" data-testid="referrals-code">
                        {code}
                      </p>
                    )}
                    <CopyButton
                      value={code}
                      label="Copy code"
                      testId="referrals-copy-code"
                      size="sm"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="flex-1 min-w-0 rounded-xl border bg-white p-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Invite link</p>
                  <p className="num mt-1 truncate text-sm font-medium" data-testid="referrals-link" title={link}>
                    {link || "\u2014"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <CopyButton value={link} label="Copy link" testId="referrals-copy-link" size="default" />
                  <Button
                    type="button"
                    onClick={nativeShare}
                    className="btn-lift gap-2"
                    data-testid="referrals-share-button"
                    disabled={!link}
                  >
                    <Share2 className="h-4 w-4" /> Share
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* ---- Earnings summary ---- */}
        <div className="space-y-5 lg:col-span-5">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Trophy className="h-4 w-4 text-amber-700" /> Your earnings
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground">INR total</p>
                <p className="num mt-1 text-xl font-semibold" data-testid="referrals-earnings-inr">
                  {fmtMoney(earningsINR, "INR")}
                </p>
              </div>
              <div className="rounded-xl bg-secondary p-3">
                <p className="text-xs text-muted-foreground">USDT total</p>
                <p className="num mt-1 text-xl font-semibold" data-testid="referrals-earnings-usdt">
                  {fmtMoney(earningsUSDT, "USDT")}
                </p>
              </div>
              <div className="col-span-2 flex items-center gap-3 rounded-xl border p-3">
                <Users className="h-5 w-5 text-primary" />
                <div className="flex-1 text-sm">
                  <p className="num font-medium" data-testid="referrals-total-friends">
                    {data?.total_friends ?? 0} friends joined
                  </p>
                  <p className="text-xs text-muted-foreground" data-testid="referrals-deposited-friends">
                    {data?.deposited_friends ?? 0} made their first deposit
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">How it works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Step
                n={1}
                title="Share your code or link"
                body="Your friends enter the code on sign-up, or just use the link."
              />
              <Step
                n={2}
                title={`Earn ${fmtMoney(cfg.first_deposit_reward, "INR")} on first deposit`}
                body="Credited to your INR wallet as soon as our team approves their first deposit."
              />
              <Step
                n={3}
                title={`Keep earning ${cfg.lifetime_pct}% — forever`}
                body="Every deposit they make after that pays you a lifetime commission."
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ---- Friends table ---- */}
      <div className="mt-6">
        <h2 className="mb-3 text-base font-semibold">Your friends</h2>
        <Card data-testid="referrals-friends-list">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6"><Skeleton className="h-40" /></div>
            ) : !data?.friends?.length ? (
              <div className="p-6">
                <EmptyState
                  icon={Gift}
                  title="No friends have joined yet"
                  testId="referrals-empty"
                >
                  Share your code to earn your first {fmtMoney(cfg.first_deposit_reward, "INR")} reward.
                </EmptyState>
              </div>
            ) : (
              <ul className="divide-y">
                {data.friends.map((f, i) => (
                  <li
                    key={i}
                    className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6"
                    data-testid="referrals-friend-row"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                      {f.name?.[0]?.toUpperCase() || "F"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{f.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Joined {fmtDate(f.joined_at, false)} · {timeAgo(f.joined_at)}
                      </p>
                    </div>
                    <div className="flex min-w-[140px] flex-col items-end text-xs">
                      {f.deposited ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800">
                          <Check className="h-3 w-3" /> First deposit done
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-800">
                          Awaiting first deposit
                        </span>
                      )}
                      <p className="num mt-1 text-right text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{fmtMoney(f.earned?.INR || 0, "INR")}</span>
                        {(f.earned?.USDT || 0) > 0 && (
                          <>
                            {" "}+{" "}
                            <span className="font-medium text-foreground">
                              {fmtMoney(f.earned.USDT, "USDT")}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <ExternalLink className="h-3 w-3" />
          Earnings land directly in your wallet. See the full ledger in <a className="underline" href="/transactions">Transactions</a>.
        </p>
      </div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="flex gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
        {n}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}
